import React, { useState, useEffect } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { X, Save, Eye, EyeOff, Hash, Type, Code, Timer } from 'lucide-react';

const SnippetEditor = ({ snippet, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    tag: '',
    title: '',
    content: '',
    language: 'javascript',
    typingSpeed: 1
  });
  
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (snippet) {
      setFormData({
        tag: snippet.tag || '',
        title: snippet.title || '',
        content: snippet.content || '',
        language: snippet.language || 'javascript',
        typingSpeed: snippet.typingSpeed || 1
      });
    }
  }, [snippet]);

  const languages = [
    { value: 'javascript', label: 'JavaScript' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'cpp', label: 'C++' },
    { value: 'html', label: 'HTML' },
    { value: 'css', label: 'CSS' },
    { value: 'json', label: 'JSON' },
    { value: 'markdown', label: 'Markdown' },
    { value: 'sql', label: 'SQL' },
    { value: 'text', label: 'Plain Text' }
  ];

  const validateTag = (tag) => {
    // Tag must start with / and be at least 4 characters (including /), and only contain allowed characters
    const tagRegex = /^\/[a-zA-Z0-9_-]{3,}$/;
    return tagRegex.test(tag);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.tag.trim()) {
      newErrors.tag = 'Tag is required';
    } else if (!validateTag(formData.tag)) {
      newErrors.tag = 'Tag must start with /, be at least 4 characters, and contain only letters, numbers, underscore, or dash';
    }
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    // Do not trim content for validation, just check for empty string
    if (!formData.content) {
      newErrors.content = 'Content is required';
    }
    
    if (formData.typingSpeed < 0 || formData.typingSpeed > 1000) {
      newErrors.typingSpeed = 'Typing speed must be between 10ms and 1000ms';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving snippet:', error);
    }
  };

  const handleTagChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, tag: value });
  };

  const detectLanguage = (content) => {
    const trimmedContent = content.trim().toLowerCase();
    
    if (trimmedContent.includes('def ') || trimmedContent.includes('import ')) return 'python';
    if (trimmedContent.includes('function ') || trimmedContent.includes('const ')) return 'javascript';
    if (trimmedContent.includes('public class ') || trimmedContent.includes('import java')) return 'java';
    if (trimmedContent.includes('#include') || trimmedContent.includes('int main')) return 'cpp';
    if (trimmedContent.includes('<html>') || trimmedContent.includes('<!DOCTYPE')) return 'html';
    if (trimmedContent.includes('select ') || trimmedContent.includes('from ')) return 'sql';
    if (trimmedContent.startsWith('{') || trimmedContent.startsWith('[')) return 'json';
    if (trimmedContent.includes('# ') || trimmedContent.includes('## ')) return 'markdown';
    
    return 'text';
  };

  const handleContentChange = (e) => {
    const content = e.target.value;
    setFormData({ ...formData, content });
    
    // Auto-detect language if not manually set
    if (!snippet && content.trim()) {
      const detectedLanguage = detectLanguage(content);
      setFormData(prev => ({ ...prev, content, language: detectedLanguage }));
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden max-h-[90vh] flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {snippet ? 'Edit Snippet' : 'Create New Snippet'}
          </h2>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {showPreview ? (
                <>
                  <EyeOff className="w-4 h-4 mr-1" />
                  Hide Preview
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-1" />
                  Show Preview
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Tag and Title Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Hash className="w-4 h-4 inline mr-1" />
                Tag
              </label>
              <input
                type="text"
                value={formData.tag}
                onChange={handleTagChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.tag ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="/py1"
              />
              {errors.tag && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.tag}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Type className="w-4 h-4 inline mr-1" />
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.title ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="Python Hello World"
              />
              {errors.title && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.title}</p>}
            </div>
          </div>

          {/* Language and Typing Speed Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Code className="w-4 h-4 inline mr-1" />
                Language
              </label>
              <select
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {languages.map((lang) => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Timer className="w-4 h-4 inline mr-1" />
                Typing Speed (ms)
              </label>
              <input
                type="number"
                min="10"
                max="1000"
                value={formData.typingSpeed}
                onChange={(e) => setFormData({ ...formData, typingSpeed: parseInt(e.target.value) || 80 })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.typingSpeed ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder="80"
              />
              {errors.typingSpeed && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.typingSpeed}</p>}
            </div>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Content
            </label>
            <div className="grid grid-cols-1 gap-4">
              <div className={showPreview ? 'md:grid-cols-2 grid gap-4' : ''}>
                <div>
                  <textarea
                    value={formData.content}
                    onChange={handleContentChange}
                    rows={showPreview ? 15 : 20}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm ${
                      errors.content ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    placeholder="Enter your code or text here..."
                  />
                  {errors.content && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.content}</p>}
                </div>
                
                {showPreview && (
                  <div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">Preview:</div>
                    <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                      <SyntaxHighlighter
                        language={formData.language}
                        style={tomorrow}
                        customStyle={{
                          margin: 0,
                          maxHeight: '400px',
                          fontSize: '13px'
                        }}
                      >
                        {formData.content || 'Type content to see preview...'}
                      </SyntaxHighlighter>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <Save className="w-4 h-4 mr-2" />
            {snippet ? 'Update Snippet' : 'Create Snippet'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SnippetEditor;
